import SideModal from "components/SideModal";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import React, { ChangeEvent, useEffect, useState } from "react";
import {
  editCourtAppeal,
  fetchAssignedProsecutor,
} from "services/advocateComplaintService";
import {
  AdvocateComplaints,
  AssignedProsecutor,
  CourtAppeal,
  Prosecutor,
} from "types";
import { LoadingIcon } from "utils/constant";
import { ADVOCATE_COURT_APPEAL_STATUS } from "utils/constantValues";
import { useTranslation } from "contexts/i18nContext";

interface DecisionModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  summonData: CourtAppeal;
  complaintData: AdvocateComplaints;
  onRefresh: any;
  onComplaintsRefresh: any;
}

export default function CourtSummonConfirmationModal({
  isOpen,
  setIsOpen,
  complaintData,
  summonData,
  onRefresh,
  onComplaintsRefresh,
}: DecisionModalProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { user } = useAuth();

  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  // const [error, setError] = useState<string | null>(null);
  const [prosecutorsData, setProsecutorsData] = useState<AssignedProsecutor[]>(
    [],
  );
  // const [showDropdown, setShowDropdown] = useState(false);

  const [formState, setFormState] = useState({
    assigned_prosecutor_id: "",
    assigned_prosecutor: null as Prosecutor | null,
    status: ADVOCATE_COURT_APPEAL_STATUS.ASSIGNED_TO_PROSECUTOR,
  });

  useEffect(() => {
    const loadProsecutors = async () => {
      try {
        const data = await fetchAssignedProsecutor(
          user?.admin_unit,
          user?.org_unit,
          user?.access,
        );

        setProsecutorsData(data);
        // setError("");
      } catch (err) {
        // setError("Failed to load prosecutors.");
      } finally {
        setLoading(false);
      }
    };
    loadProsecutors();
  }, [user]);

  const filtered = prosecutorsData?.filter(
    (p) =>
      `${p?.user?.first_name} ${p?.user?.last_name}`
        .toLowerCase()
        .includes(query.toLowerCase()) &&
      p?.roles?.includes("AdvocateDisciplineProsecutor"),
  );

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelect = (prosecutor: Prosecutor, name: string) => {
    setFormState((prevState) => ({
      ...prevState,
      [name]: prosecutor,
    }));
    setQuery("");
    // setShowDropdown(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!summonData) return;

    setLoading(true);

    const data = new FormData();
    data.append(
      "assigned_prosecutor",
      `${formState?.assigned_prosecutor?.first_name || ""} ${
        formState?.assigned_prosecutor?.last_name || ""
      }`,
    );
    data.append(
      "userid_assigned_prosecutor",
      formState?.assigned_prosecutor_id || "",
    );
    data.append("status", formState.status ?? "");
    data.append("admin_unit_id", complaintData?.admin_unit_id || "");
    data.append("org_id", complaintData?.org_id || "");
    data.append("accused_advocate_name", complaintData?.accused_name || "");
    data.append(
      "user_id_accused_advocate",
      complaintData?.userid_accused_name || "",
    );
    data.append(
      "accused_license_number",
      complaintData?.accused_license_number || "",
    );

    const summon_id = summonData?.id || "";
    const isFormData = true;

    try {
      await editCourtAppeal(data, summon_id, isFormData);

      onRefresh();
      onComplaintsRefresh();
      setIsOpen(false);
      showToast(t("confirmed"), "success");
      setFormState({
        assigned_prosecutor_id: "",
        assigned_prosecutor: null as Prosecutor | null,
        status: ADVOCATE_COURT_APPEAL_STATUS.ASSIGNED_TO_PROSECUTOR,
      });
    } catch (err: any) {
      console.error("Submission error:", err);
      showToast(t("submissionError"), "error");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <SideModal
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      title={t("confirmCourtSummon")}
    >
      <form onSubmit={handleSubmit} className="evidence-form">
        <section className="evidence-section">
          <div className="mb-4">
            <label className="text-sm font-medium text-gray-700 mb-1 block">
              {t("assignProsecutor")}
            </label>
            <select
              required
              className="w-full border bg-gray-100 rounded-md p-2 text-sm"
              name="assigned_prosecutor_id"
              value={formState.assigned_prosecutor_id}
              onChange={(e) => {
                handleChange(e);
                const selected = filtered?.find(
                  (p) => p.user.id?.toString() === e.target.value.toString(),
                );

                console.log(selected);
                if (selected && selected.user) {
                  handleSelect(selected.user, "assigned_prosecutor");
                }
              }}
            >
              <option value={""}>{t("selectProsecutor")}</option>
              {filtered?.map((p) => (
                <option key={p.user.id} value={p.user.id}>
                  {p.user?.first_name} ({p.user?.last_name})
                </option>
              ))}
            </select>
          </div>
        </section>

        <button
          type="submit"
          className="bg-black w-full text-white px-6 py-2 rounded-md hover:bg-gray-800"
          disabled={loading}
        >
          {loading ? (
            <div className="flex items-center justify-center">
              {LoadingIcon}
              {t("submitting")}
            </div>
          ) : (
            t("assign")
          )}
        </button>
      </form>
    </SideModal>
  );
}
